import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Play, Users, Search } from "lucide-react";
import { HERO_IMG } from "../../lib/site";
import { useLead, scrollToId, BrutalButton, KW } from "./shared";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const Line = ({ children }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineChild} className="block">{children}</motion.span>
  </span>
);

const TRUST = ["12-Month Program", "Live Mentorship", "Community Support", "Regular Feedback"];

export default function Hero() {
  const { openLead } = useLead();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const floatY1 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const floatY2 = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section ref={ref} className="relative bg-brand-cream pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 px-5 sm:px-8 md:px-12 overflow-hidden">
      {/* decorative shapes */}
      <div className="pointer-events-none absolute -top-10 right-1/3 h-40 w-40 rounded-full bg-brand-yellow/40 blur-2xl" />
      <div className="pointer-events-none absolute bottom-10 left-1/4 h-32 w-32 rounded-full bg-brand-blue/10 blur-2xl" />

      <div className="max-w-[1320px] mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-8 items-center">
        {/* LEFT */}
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border-2 border-brand-black bg-white px-4 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em]">
            <span className="h-2 w-2 rounded-full bg-brand-red animate-pulse" />
            12-Month YouTube Mentorship
          </motion.div>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-display mt-5 text-[38px] sm:text-[54px] md:text-[64px] lg:text-[76px] leading-[0.98] tracking-tight"
          >
            <Line><KW c="#0B31EB">Turn Your </KW><KW c="#4CAF48">Expertise</KW></Line>
            <Line><KW c="#0B31EB">Into Searchable</KW></Line>
            <Line><KW c="#E90019">Authority</KW></Line>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-brand-ink/80 leading-relaxed">
            Build a <span className="font-semibold text-brand-black">YouTube</span> channel that attracts high-intent viewers, earns trust, and turns the right people into leads and paying customers—without depending entirely on paid ads.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3">
            <BrutalButton onClick={openLead} variant="red" data-testid="hero-join-button">
              Join the Current Batch
            </BrutalButton>
            <BrutalButton onClick={() => scrollToId("system")} variant="outline" data-testid="hero-system-button">
              See How the System Works <ArrowDown size={16} />
            </BrutalButton>
          </motion.div>

          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.6 }}
            className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {TRUST.map((t) => (
              <li key={t} className="flex items-center gap-2 rounded-full border-2 border-brand-black/15 bg-white px-3 py-2 text-xs sm:text-[13px] font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-green" /> {t}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* RIGHT — image + floats */}
        <div className="relative">
          <motion.div style={{ y: imgY }} className="relative">
            <div className="relative rounded-[30px] border-2 border-brand-black overflow-hidden shadow-[12px_14px_0_#0B31EB] bg-brand-red">
              <img
                src={HERO_IMG}
                alt="Ratish Jha holding a YouTube play button, surrounded by YouTube and AI icons"
                className="w-full h-full object-cover aspect-square"
                fetchPriority="high"
              />
            </div>
          </motion.div>

          {/* Floating card 1 */}
          <motion.div style={{ y: floatY1 }} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.1, type: "spring", stiffness: 200, damping: 16 }}
            className="absolute -left-2 sm:-left-6 top-8 bg-white border-2 border-brand-black rounded-2xl px-4 py-3 shadow-[6px_8px_0_#111111]">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-xl bg-brand-blue flex items-center justify-center"><Search size={16} className="text-white" /></span>
              <div>
                <p className="font-display text-lg leading-none">24/7</p>
                <p className="text-[11px] font-semibold text-brand-ink/60">Searchable Trust</p>
              </div>
            </div>
          </motion.div>

          {/* Floating card 2 */}
          <motion.div style={{ y: floatY2 }} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.25, type: "spring", stiffness: 200, damping: 16 }}
            className="absolute right-2 sm:right-3 bottom-10 bg-brand-yellow border-2 border-brand-black rounded-2xl px-4 py-3 shadow-[6px_8px_0_#111111]">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-xl bg-brand-black flex items-center justify-center"><Users size={16} className="text-brand-yellow" /></span>
              <div>
                <p className="font-display text-base leading-none">High Intent</p>
                <p className="text-[11px] font-semibold text-brand-ink/70">Relevant Viewers</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
            className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-brand-black text-white rounded-full px-4 py-2 flex items-center gap-2 border-2 border-brand-black">
            <Play size={13} className="fill-white" /><span className="text-xs font-bold">Right video · Right person · Right time</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
